/**
 * Reflection scan — mirror-pair discovery + bind-frame parity comparison
 * (ADR-008 §2e). First resident of kernel/mapping: this is ADR-004 §1
 * "geometric evidence" (positional L/R symmetry), exported so adapters can
 * construct RigDescriptors whose required `reflectedPairs` field is honest
 * from birth — "detection ran" must be true the moment the descriptor exists.
 *
 * Depends on: ir/rig-descriptor (types), retarget/convention, math/vec3.
 * Depended on by: adapters (at conversion), kernel/mapping (evidence, later).
 *
 * Detection law: names PROPOSE (side-token pairing), geometry CONFIRMS
 * (positional symmetry across the declared sagittal plane), parity DECIDES
 * (bind-frame determinant signs). A pair is reflection-related iff exactly
 * one side's bind frame is improper — two proper frames are always related
 * by a proper rotation (SO(3) is a group), so parity mismatch is the whole
 * story at this stage; behaved mirroring is the frame-map law's job.
 */
import type { RigBone, RigDescriptor } from '../ir/rig-descriptor.js';
import { conventionTriad } from '../retarget/convention.js';
import { vec3Dot, vec3Sub, vec3Scale, type Vec3Data } from '../math/vec3.js';
import { SchemaValidationError } from '../ir/errors.js';
import { sideToken, type Side } from './normalize.js';

export interface ReflectionScan {
  /** Confirmed mirror pairs whose bind frames are reflection-related (parity mismatch). */
  readonly reflectedPairs: ReadonlyArray<readonly [string, string]>;
  /** Every name-proposed, geometry-confirmed mirror pair (left name first). */
  readonly mirrorPairs: ReadonlyArray<readonly [string, string]>;
  /** ALL bones with improper (negative-determinant) bind frames, paired or not. */
  readonly improperBones: readonly string[];
  /** Side-marked names the pairing had to skip (duplicate keys, missing partner, failed geometry). */
  readonly unpairedSided: readonly string[];
}

export interface ReflectionScanOptions {
  /**
   * Geometric confirmation tolerance as a fraction of the rig's bind-pose
   * bounding-box diagonal (default 0.05). Names propose; a candidate pair is
   * confirmed only if mirroring one position across the sagittal plane lands
   * within this distance of its partner.
   */
  readonly positionTolerance?: number;
}

/**
 * Parity of a bind frame from its decomposed scale: −1 when the linear part
 * has negative determinant (an odd number of negative scale components) —
 * quaternions can only encode proper rotations, so decomposition shoves any
 * reflection into the scale signs. Zero scale carries no parity: loud.
 */
export function bindFrameParity(scale: Vec3Data): 1 | -1 {
  const det = scale.x * scale.y * scale.z;
  if (det === 0) {
    throw new SchemaValidationError('ReflectionScan', [
      'bind scale has a zero component — the frame has no parity (degenerate bind)',
    ]);
  }
  return det < 0 ? -1 : 1;
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

/** Bind-pose bounding-box diagonal — the scale for relative tolerances. */
export function rigExtent(bones: readonly RigBone[]): number {
  let sum = 0;
  for (const axis of ['x', 'y', 'z'] as const) {
    const values = bones.map((b) => b.worldBind.position[axis]);
    const range = Math.max(...values) - Math.min(...values);
    sum += range * range;
  }
  return Math.sqrt(sum);
}

/**
 * Where the sagittal plane sits along `sideAxis`: the median MIDPOINT of the
 * name-proposed L/R pairs (one left, one right per key) — the body's own
 * centre, which an unsided appendage cannot move. The median of EVERY bone
 * let one do it: FingerArmed/Hamish.glb (a 2026-09-24 export) has a 16-bone
 * tail curled to one side that dragged it 3.7 cm off the hips, and on a body
 * 3 cm asymmetric of its own 34 of 38 pairs failed, the hips read off-centre,
 * the spine went unmapped and the file refused ("Hips is unmapped"). Every
 * bone's median remains the fallback for a rig with no proposed pair.
 * Shared by the pairing below and the mapper's side coordinates (map-rig).
 */
export function sagittalPlaneOffset(bones: readonly RigBone[], sideAxis: Vec3Data): number {
  const positionOf = new Map(bones.map((b) => [b.name, b.worldBind.position]));
  const byKey = new Map<string, { left: string[]; right: string[] }>();
  for (const b of bones) {
    const token = sideToken(b.name);
    if (token === null) continue;
    const bucket = byKey.get(token.key) ?? { left: [], right: [] };
    bucket[token.side].push(b.name);
    byKey.set(token.key, bucket);
  }
  const midpoints: number[] = [];
  for (const bucket of byKey.values()) {
    if (bucket.left.length !== 1 || bucket.right.length !== 1) continue;
    const pl = positionOf.get(bucket.left[0] as string) as Vec3Data;
    const pr = positionOf.get(bucket.right[0] as string) as Vec3Data;
    midpoints.push((vec3Dot(pl, sideAxis) + vec3Dot(pr, sideAxis)) / 2);
  }
  return median(midpoints.length > 0 ? midpoints : bones.map((b) => vec3Dot(b.worldBind.position, sideAxis)));
}

/**
 * Mirror pairing across a GIVEN candidate sagittal-plane normal: names
 * propose (side tokens), geometry confirms (positional symmetry across the
 * plane at the median MIDPOINT of the name-proposed pairs). Exposed so mapping evidence can test
 * candidate axes — the Tripo corpus discovery: rigs lie about facing, so the
 * declared side axis is a hypothesis, not a fact (ADR-004 §1 geometric).
 */
export function mirrorPairsAcrossAxis(
  bones: readonly RigBone[],
  sideAxis: Vec3Data,
  options: ReflectionScanOptions = {},
): Pick<ReflectionScan, 'mirrorPairs' | 'unpairedSided'> {
  const tolerance = options.positionTolerance ?? 0.05;
  const positionOf = new Map(bones.map((b) => [b.name, b.worldBind.position]));

  const extent = rigExtent(bones);

  // Names propose…
  const byKey = new Map<string, { left: string[]; right: string[] }>();
  const sided = new Map<string, Side>();
  for (const b of bones) {
    const token = sideToken(b.name);
    if (token === null) continue;
    sided.set(b.name, token.side);
    const bucket = byKey.get(token.key) ?? { left: [], right: [] };
    bucket[token.side].push(b.name);
    byKey.set(token.key, bucket);
  }
  const proposed = [...byKey.values()].filter((bucket) => bucket.left.length === 1 && bucket.right.length === 1); // not ambiguous, not partnerless
  const planeOffset = sagittalPlaneOffset(bones, sideAxis);

  // …geometry confirms.
  const mirrorPairs: Array<readonly [string, string]> = [];
  const paired = new Set<string>();
  for (const bucket of proposed) {
    const left = bucket.left[0] as string;
    const right = bucket.right[0] as string;
    const pl = positionOf.get(left) as Vec3Data;
    const pr = positionOf.get(right) as Vec3Data;
    const mirrored = vec3Sub(pl, vec3Scale(sideAxis, 2 * (vec3Dot(pl, sideAxis) - planeOffset)));
    const gap = vec3Sub(mirrored, pr);
    if (Math.sqrt(vec3Dot(gap, gap)) <= tolerance * extent) {
      mirrorPairs.push([left, right] as const);
      paired.add(left);
      paired.add(right);
    }
  }
  const unpairedSided = [...sided.keys()].filter((name) => !paired.has(name)).sort();
  return { mirrorPairs, unpairedSided };
}

/**
 * Scan a bone set for mirror pairs and reflection-related bind frames, using
 * the rig's DECLARED conventions to locate the sagittal plane (side axis =
 * up × facing; plane offset = `sagittalPlaneOffset`, the median L/R pair midpoint).
 * Pure and deterministic; safe on any bone list an adapter can produce.
 */
export function detectReflectedPairs(
  bones: readonly RigBone[],
  convention: Pick<RigDescriptor, 'upAxis' | 'facing'>,
  options: ReflectionScanOptions = {},
): ReflectionScan {
  const triad = conventionTriad(convention);
  if (triad === null) {
    throw new SchemaValidationError('ReflectionScan', [
      `degenerate convention: upAxis ${convention.upAxis} with facing ${convention.facing} names no sagittal plane`,
    ]);
  }
  const improperBones = bones.filter((b) => bindFrameParity(b.worldBind.scale) === -1).map((b) => b.name);
  const parityOf = new Map(bones.map((b) => [b.name, bindFrameParity(b.worldBind.scale)]));

  const { mirrorPairs, unpairedSided } = mirrorPairsAcrossAxis(bones, triad.side, options);
  const reflectedPairs = mirrorPairs.filter(
    ([left, right]) => (parityOf.get(left) as number) * (parityOf.get(right) as number) < 0,
  );
  return { reflectedPairs, mirrorPairs, improperBones, unpairedSided };
}
